import Button from 'admin/components/atoms/Buttons';
import FA from 'admin/components/atoms/FontAwesome';
import Input from 'admin/components/atoms/Input';
import Select from 'admin/components/atoms/Select';
import Table from 'admin/components/atoms/Table';
import BreadCrumb from 'admin/components/molecules/BreadCrumb';
import EditableTableCell from 'admin/components/molecules/EditableTableCell';
import React, { useCallback, useEffect, useState } from 'react';
import Modal from 'react-modal';
import { useParams } from 'react-router-dom';
import { Alert } from 'reactstrap';
import styled, { css } from 'styled-components';
import {
  createUserTagGroupItem,
  createUserTagSubgroup,
  deleteUserTagGroupItem,
  deleteUserTagSubgroup,
  getUserTagGroupDetail,
  updateUserTagGroupItem,
  updateUserTagSubgroup,
  UserTagGroupItem,
  UserTagSubgroup,
} from '~/javascripts/admin/actions/user-tag-group';
import { useAppSelector } from '~/javascripts/admin/hooks/useApp';
import { selectUserPrivateMoney } from '~/javascripts/admin/reducers/user-private-money';

type LabelItem = {
  id: string;
  name: string;
  // 所属 subgroup。未分類（グループ直下）は null。
  subgroupId: string | null;
};

type Subgroup = {
  id: string;
  name: string;
};

// 未分類（グループ直下）を表す Select 上のセンチネル値。
const UNGROUPED_VALUE = '__ungrouped__';
const UNGROUPED_LABEL = '未分類（グループ直下）';

const modalStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-30%',
    transform: 'translate(-50%, -50%)',
    minWidth: '400px',
    padding: '24px',
  },
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
};

const AddButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-bottom: 16px;
`;

const PageHeader = styled.h1`
  font-size: ${({ theme }) => theme.typography.heading3.bold.fontSize};
  font-weight: ${({ theme }) => theme.typography.heading3.bold.fontWeight};
  line-height: ${({ theme }) => theme.typography.heading3.bold.lineHeight};
  color: ${({ theme }) => theme.colors.gray['400']};
  margin-bottom: 24px;
`;

const SectionHeader = styled.h2`
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.gray['400']};
  margin: 32px 0 12px;
`;

// subgroup ごとの見出し（ラベル一覧内）
const SubgroupCaption = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.gray['400']};
  padding: 8px 0 4px;
  margin-top: 8px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray['200']};
`;

const SectionHint = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.gray['300']};
  margin-bottom: 8px;
`;

const EmptySectionText = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.gray['300']};
  padding: 8px 0 16px;
`;

const ModalTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.heading3.bold.fontSize};
  font-weight: ${({ theme }) => theme.typography.heading3.bold.fontWeight};
  color: ${({ theme }) => theme.colors.gray['400']};
  margin-bottom: 16px;
  text-align: center;
`;

const FormRow = styled.div`
  margin-bottom: 16px;
`;

const FormLabel = styled.label`
  display: block;
  font-size: 14px;
  margin-bottom: 4px;
  color: ${({ theme }) => theme.colors.gray['400']};
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.colors.gray['200']};
  border-radius: 4px;
  font-size: 14px;
  resize: vertical;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.gray['400']};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.gray['200']};
  }
`;

const ModalButtonGroup = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 16px;
`;

// テーブル内の操作ボタンサイズを統一する（編集/削除）。
const ActionButton = styled(Button)`
  min-width: 56px;
  padding: 4px 12px;
  font-size: 14px;
`;

// 削除は破壊的操作のため、白背景・赤枠・赤文字にする。
const DeleteButton = styled(ActionButton)`
  border-color: #d75f48;
  color: #d75f48;
  &:hover {
    background: #fdf3f1;
  }
`;

const ActionGroup = styled.div`
  display: flex;
  gap: 8px;
`;

// テーブル内のテキストサイズを統一する
const tableTextStyle = css`
  font-size: 14px;
`;

const LoadingText = styled.div`
  color: ${({ theme }) => theme.colors.gray['300']};
  padding: 16px 0;
`;

const WarningMessage = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  color: ${({ theme }) => theme.semanticColors.error};
  margin-bottom: 16px;
`;

const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => theme.semanticColors.error};
  font-size: 14px;
  margin-top: 8px;
`;

// 削除済み（is_active=false）の subgroup を指す item は未分類として扱う。
// group 詳細の subgroups[] はアクティブのみのため、そうしないとどのセクションにも属さず表示から漏れる。
const mapItemToLabelItem = (item: UserTagGroupItem): LabelItem => ({
  id: item.id,
  name: item.name_ja || item.name_en,
  subgroupId:
    item.user_tag_subgroup && item.user_tag_subgroup.is_active ? item.user_tag_subgroup.id : null,
});

const mapSubgroup = (sg: UserTagSubgroup): Subgroup => ({
  id: sg.id,
  name: sg.name_ja || sg.name_en,
});

// throwOnError は 4xx で response.json()（Promise）を、5xx で文字列を throw する。
// どちらでも message を取り出せるよう Promise.resolve で吸収する。
const extractErrorMessage = async (error: unknown, fallback: string): Promise<string> => {
  try {
    const err = await Promise.resolve(error as Promise<{ message?: string }>);
    return (err && err.message) || fallback;
  } catch {
    return fallback;
  }
};

const SettingsShopLabelDetail = () => {
  const { uuid } = useParams<{ uuid: string }>();

  const { organization: selectedOrg } = useAppSelector(selectUserPrivateMoney);

  const [groupName, setGroupName] = useState('');
  const [groupOrg, setGroupOrg] = useState<{ code: string; name: string } | null>(null);
  const [items, setItems] = useState<LabelItem[]>([]);
  const [subgroups, setSubgroups] = useState<Subgroup[]>([]);
  const [editingValues, setEditingValues] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pendingNameUpdate, setPendingNameUpdate] = useState<string | null>(null);
  const [deletingItem, setDeletingItem] = useState<LabelItem | null>(null);
  const [showUpdateSuccess, setShowUpdateSuccess] = useState(false);
  const [showAddSuccess, setShowAddSuccess] = useState(false);
  const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);
  const [showDuplicateNameError, setShowDuplicateNameError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // ラベル追加モーダル
  const [isItemModalOpen, setIsItemModalOpen] = useState(false);
  const [labelText, setLabelText] = useState('');
  const [addItemSubgroupId, setAddItemSubgroupId] = useState<string | null>(null);
  const [addDuplicateError, setAddDuplicateError] = useState(false);

  // サブグループ 追加/編集/削除モーダル
  const [isSubgroupAddOpen, setIsSubgroupAddOpen] = useState(false);
  const [subgroupName, setSubgroupName] = useState('');
  const [subgroupAddError, setSubgroupAddError] = useState<string | null>(null);
  const [editingSubgroup, setEditingSubgroup] = useState<Subgroup | null>(null);
  const [editSubgroupName, setEditSubgroupName] = useState('');
  const [editSubgroupError, setEditSubgroupError] = useState<string | null>(null);
  const [deletingSubgroup, setDeletingSubgroup] = useState<Subgroup | null>(null);

  const fetchDetail = useCallback(async () => {
    if (!uuid) return;

    setLoading(true);
    try {
      const detail = await getUserTagGroupDetail(uuid);
      setGroupName(detail.name_ja || detail.name_en);
      setGroupOrg(detail.organization ?? null);
      setItems(detail.items.map(mapItemToLabelItem));
      // group 詳細の subgroups はアクティブのみ・作成順。
      setSubgroups((detail.subgroups ?? []).map(mapSubgroup));
    } catch (error) {
      setItems([]);
      setSubgroups([]);
      const message = await extractErrorMessage(error, 'ラベルの取得に失敗しました');
      setErrorMessage(message);
    } finally {
      setLoading(false);
    }
  }, [uuid]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const crumbs = [
    { value: 'ホーム', href: '/', isCurrentPage: false },
    { value: '各種設定', href: '', isCurrentPage: true },
    { value: '店舗ラベル管理', href: '/settings/shop-label', isCurrentPage: false },
    { value: groupName || 'ラベル一覧', href: '', isCurrentPage: true },
  ];

  // グループの所属発行体と、ヘッダで選択中のマネーの発行体が相違する場合は注意喚起する
  const issuerMismatch =
    !!groupOrg?.code && !!selectedOrg?.code && groupOrg.code !== selectedOrg.code;

  const hasSubgroups = subgroups.length > 0;

  const flashUpdateSuccess = () => {
    setShowUpdateSuccess(true);
    setTimeout(() => setShowUpdateSuccess(false), 3000);
  };

  const subgroupNameOf = (subgroupId: string | null): string =>
    subgroups.find((sg) => sg.id === subgroupId)?.name ?? '';

  // 同一 subgroup 内での重複名を判定（未分類同士も比較）。
  const isDuplicateItemName = (name: string, subgroupId: string | null, excludeId?: string) =>
    items.some(
      (item) => item.id !== excludeId && item.subgroupId === subgroupId && item.name === name
    );

  // --- ラベル追加 ---
  const openItemModal = (subgroupId: string | null) => {
    setAddItemSubgroupId(subgroupId);
    setIsItemModalOpen(true);
  };
  const closeItemModal = () => {
    setIsItemModalOpen(false);
    setLabelText('');
    setAddItemSubgroupId(null);
    setAddDuplicateError(false);
  };

  const handleAddItem = async () => {
    const name = labelText.trim();
    if (!name || !uuid) return;

    if (isDuplicateItemName(name, addItemSubgroupId)) {
      setAddDuplicateError(true);
      return;
    }

    setSaving(true);
    try {
      const newItem = await createUserTagGroupItem(uuid, {
        name_ja: name,
        name_en: name,
        user_tag_subgroup_id: addItemSubgroupId,
      });
      setItems((prev) => [...prev, mapItemToLabelItem(newItem)]);
      closeItemModal();
      setShowAddSuccess(true);
      setTimeout(() => setShowAddSuccess(false), 3000);
    } catch (error) {
      const message = await extractErrorMessage(error, 'ラベルの追加に失敗しました');
      setErrorMessage(message);
    } finally {
      setSaving(false);
    }
  };

  // --- ラベル名 インライン編集 ---
  const startEditing = (id: string, currentName: string) => {
    setEditingValues((prev) => ({ ...prev, [id]: currentName }));
  };

  const handleNameCancel = (id: string) => {
    setEditingValues((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const handleNameUpdate = (id: string) => {
    const newName = editingValues[id];
    if (newName === undefined) return;

    if (!newName.trim()) {
      handleNameCancel(id);
      return;
    }

    const target = items.find((item) => item.id === id);
    if (target && isDuplicateItemName(newName.trim(), target.subgroupId, id)) {
      setShowDuplicateNameError(true);
      return;
    }

    setPendingNameUpdate(id);
  };

  const confirmNameUpdate = async () => {
    if (!pendingNameUpdate) return;

    const id = pendingNameUpdate;
    const newName = editingValues[id];
    if (newName === undefined) return;

    const originalName = items.find((item) => item.id === id)?.name;

    setPendingNameUpdate(null);
    setItems((prev) => prev.map((item) => (item.id === id ? { ...item, name: newName } : item)));
    setEditingValues((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });

    try {
      await updateUserTagGroupItem(id, { name_ja: newName, name_en: newName });
      flashUpdateSuccess();
    } catch (error) {
      if (originalName !== undefined) {
        setItems((prev) =>
          prev.map((item) => (item.id === id ? { ...item, name: originalName } : item))
        );
      }
      const message = await extractErrorMessage(error, 'ラベルの更新に失敗しました');
      setErrorMessage(message);
    }
  };

  const cancelNameUpdate = () => {
    setPendingNameUpdate(null);
  };

  // --- ラベルの subgroup 付け替え（インライン Select） ---
  const handleReassignSubgroup = async (item: LabelItem, nextSubgroupId: string | null) => {
    if (item.subgroupId === nextSubgroupId || saving) return;

    // 付け替え先に同名ラベルがあると追加・名称編集と同様サーバに拒否される。事前チェックで平仄を合わせる。
    if (isDuplicateItemName(item.name, nextSubgroupId, item.id)) {
      setErrorMessage('移動先に同名のラベルが既に登録されています');
      return;
    }

    // 連続付け替え時の巻き戻し競合を避けるため saving で当該操作を直列化する（行 Select は isDisabled={saving}）。
    const previous = item.subgroupId;
    setSaving(true);
    setItems((prev) =>
      prev.map((it) => (it.id === item.id ? { ...it, subgroupId: nextSubgroupId } : it))
    );
    try {
      await updateUserTagGroupItem(item.id, { user_tag_subgroup_id: nextSubgroupId });
      flashUpdateSuccess();
    } catch (error) {
      setItems((prev) =>
        prev.map((it) => (it.id === item.id ? { ...it, subgroupId: previous } : it))
      );
      const message = await extractErrorMessage(error, 'サブグループの変更に失敗しました');
      setErrorMessage(message);
    } finally {
      setSaving(false);
    }
  };

  // --- ラベル削除 ---
  const openDeleteItem = (item: LabelItem) => setDeletingItem(item);
  const closeDeleteItem = () => setDeletingItem(null);

  const handleDeleteItem = async () => {
    if (!deletingItem) return;
    setSaving(true);
    try {
      await deleteUserTagGroupItem(deletingItem.id);
      setItems((prev) => prev.filter((item) => item.id !== deletingItem.id));
      closeDeleteItem();
      setShowDeleteSuccess(true);
      setTimeout(() => setShowDeleteSuccess(false), 3000);
    } catch (error) {
      const message = await extractErrorMessage(error, 'ラベルの削除に失敗しました');
      setErrorMessage(message);
    } finally {
      setSaving(false);
    }
  };

  // --- サブグループ 追加 ---
  const openSubgroupAdd = () => {
    setSubgroupName('');
    setSubgroupAddError(null);
    setIsSubgroupAddOpen(true);
  };
  const closeSubgroupAdd = () => setIsSubgroupAddOpen(false);

  const handleAddSubgroup = async () => {
    const name = subgroupName.trim();
    if (!name || !uuid) return;
    if (subgroups.some((sg) => sg.name === name)) {
      setSubgroupAddError('同名のサブグループが既に登録されています');
      return;
    }

    setSaving(true);
    try {
      const created = await createUserTagSubgroup(uuid, { name_ja: name, name_en: name });
      setSubgroups((prev) => [...prev, mapSubgroup(created)]);
      closeSubgroupAdd();
      setShowAddSuccess(true);
      setTimeout(() => setShowAddSuccess(false), 3000);
    } catch (error) {
      const message = await extractErrorMessage(error, 'サブグループの追加に失敗しました');
      setSubgroupAddError(message);
    } finally {
      setSaving(false);
    }
  };

  // --- サブグループ 編集 ---
  const openSubgroupEdit = (sg: Subgroup) => {
    setEditingSubgroup(sg);
    setEditSubgroupName(sg.name);
    setEditSubgroupError(null);
  };
  const closeSubgroupEdit = () => setEditingSubgroup(null);

  const handleEditSubgroup = async () => {
    if (!editingSubgroup) return;
    const name = editSubgroupName.trim();
    if (!name) {
      setEditSubgroupError('サブグループ名を入力してください');
      return;
    }
    if (subgroups.some((sg) => sg.id !== editingSubgroup.id && sg.name === name)) {
      setEditSubgroupError('同名のサブグループが既に登録されています');
      return;
    }

    setSaving(true);
    try {
      await updateUserTagSubgroup(editingSubgroup.id, { name_ja: name, name_en: name });
      setSubgroups((prev) =>
        prev.map((sg) => (sg.id === editingSubgroup.id ? { ...sg, name } : sg))
      );
      closeSubgroupEdit();
      flashUpdateSuccess();
    } catch (error) {
      const message = await extractErrorMessage(error, 'サブグループの更新に失敗しました');
      setEditSubgroupError(message);
    } finally {
      setSaving(false);
    }
  };

  // --- サブグループ 削除 ---
  const openSubgroupDelete = (sg: Subgroup) => setDeletingSubgroup(sg);
  const closeSubgroupDelete = () => setDeletingSubgroup(null);

  const handleDeleteSubgroup = async () => {
    if (!deletingSubgroup) return;
    setSaving(true);
    try {
      await deleteUserTagSubgroup(deletingSubgroup.id);
      // サーバは配下 item をカスケードしない。UI 上は該当 item を未分類として扱う。
      setItems((prev) =>
        prev.map((it) =>
          it.subgroupId === deletingSubgroup.id ? { ...it, subgroupId: null } : it
        )
      );
      setSubgroups((prev) => prev.filter((sg) => sg.id !== deletingSubgroup.id));
      closeSubgroupDelete();
      setShowDeleteSuccess(true);
      setTimeout(() => setShowDeleteSuccess(false), 3000);
    } catch (error) {
      const message = await extractErrorMessage(error, 'サブグループの削除に失敗しました');
      setErrorMessage(message);
    } finally {
      setSaving(false);
    }
  };

  // subgroup 付け替え Select の選択肢（末尾に「未分類」）。
  const subgroupSelectOptions = [
    ...subgroups.map((sg) => ({ label: sg.name, value: sg.id })),
    { label: '未分類', value: UNGROUPED_VALUE },
  ];

  // ラベル一覧のセクション構成。subgroup が無ければ単一（見出しなし）。
  const ungroupedItems = items.filter((item) => item.subgroupId === null);
  const sections = hasSubgroups
    ? [
        ...subgroups.map((sg) => ({
          key: sg.id,
          subgroupId: sg.id as string | null,
          title: sg.name,
          items: items.filter((item) => item.subgroupId === sg.id),
        })),
        // 未分類は item がある場合のみ表示する（無ければノイズになるため）。
        ...(ungroupedItems.length > 0
          ? [
              {
                key: UNGROUPED_VALUE,
                subgroupId: null as string | null,
                title: UNGROUPED_LABEL,
                items: ungroupedItems,
              },
            ]
          : []),
      ]
    : [{ key: '__all__', subgroupId: null as string | null, title: null, items }];

  const renderItemRow = (item: LabelItem) => (
    <Table.Row key={item.id}>
      <Table.Cell>{item.id}</Table.Cell>
      <Table.Cell>
        <EditableTableCell
          isEditable={editingValues[item.id] !== undefined}
          onToggle={(editing) => {
            if (editing) {
              startEditing(item.id, item.name);
            }
          }}
          onUpdate={() => handleNameUpdate(item.id)}
          onCancel={() => handleNameCancel(item.id)}
          renderInput={() => (
            <Input
              fullWidth
              syncValue={editingValues[item.id] ?? item.name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const { value } = e.target;
                setEditingValues((prev) => ({ ...prev, [item.id]: value }));
              }}
            />
          )}
          renderValue={() => <span>{item.name}</span>}
        />
      </Table.Cell>
      {hasSubgroups && (
        <Table.Cell>
          <Select
            options={subgroupSelectOptions}
            value={
              item.subgroupId
                ? { label: subgroupNameOf(item.subgroupId), value: item.subgroupId }
                : { label: '未分類', value: UNGROUPED_VALUE }
            }
            onChange={(opt: { value: string } | null) =>
              handleReassignSubgroup(item, opt && opt.value !== UNGROUPED_VALUE ? opt.value : null)
            }
            isDisabled={saving}
            fullWidth
          />
        </Table.Cell>
      )}
      <Table.Cell>
        <DeleteButton variant="outline" onClick={() => openDeleteItem(item)}>
          削除
        </DeleteButton>
      </Table.Cell>
    </Table.Row>
  );

  const itemHeaders = hasSubgroups
    ? [
        { label: 'ID', size: 2 },
        { label: 'ラベル名', size: 5 },
        { label: 'サブグループ', size: 3 },
        { label: '操作', size: 2 },
      ]
    : [
        { label: 'ID', size: 2 },
        { label: 'ラベル名', size: 8 },
        { label: '操作', size: 2 },
      ];

  return (
    <>
      <BreadCrumb crumbs={crumbs} />
      {issuerMismatch && (
        <Alert color="warning">
          <FA icon="exclamation-triangle" /> 表示中のグループは発行体「{groupOrg?.name}（
          {groupOrg?.code}）」に所属しています。ヘッダで選択中のマネーの発行体「{selectedOrg?.name}
          （{selectedOrg?.code}）」とは異なります。ここでの追加・編集は「{groupOrg?.name}
          」に反映されます。
        </Alert>
      )}
      <Alert color="danger" isOpen={errorMessage !== null} toggle={() => setErrorMessage(null)}>
        {errorMessage}
      </Alert>
      <Alert color="success" isOpen={showUpdateSuccess}>
        更新しました
      </Alert>
      <Alert color="success" isOpen={showAddSuccess}>
        追加しました
      </Alert>
      <Alert color="success" isOpen={showDeleteSuccess}>
        削除しました
      </Alert>

      <PageHeader>{groupName || 'ラベル一覧'}</PageHeader>

      {loading ? (
        <LoadingText>読み込み中...</LoadingText>
      ) : (
        <>
          {/* サブグループ管理セクション */}
          <SectionHeader>サブグループ</SectionHeader>
          <SectionHint>
            サブグループを作成すると、ラベルを中分類（例：業種 → 飲食業 → レストラン）で
            整理できます。付与時の選択対象は従来どおりラベルのみです。
          </SectionHint>
          <AddButtonWrapper>
            <Button onClick={openSubgroupAdd}>サブグループを追加</Button>
          </AddButtonWrapper>
          {hasSubgroups ? (
            <Table styles={tableTextStyle}>
              <Table.Header>
                <Table.Row>
                  <Table.Cell size={8}>サブグループ名</Table.Cell>
                  <Table.Cell size={4}>操作</Table.Cell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {subgroups.map((sg) => (
                  <Table.Row key={sg.id}>
                    <Table.Cell>{sg.name}</Table.Cell>
                    <Table.Cell>
                      <ActionGroup>
                        <ActionButton variant="outline" onClick={() => openSubgroupEdit(sg)}>
                          編集
                        </ActionButton>
                        <DeleteButton variant="outline" onClick={() => openSubgroupDelete(sg)}>
                          削除
                        </DeleteButton>
                      </ActionGroup>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          ) : (
            <EmptySectionText>サブグループはまだありません。</EmptySectionText>
          )}

          {/* ラベル一覧セクション */}
          <SectionHeader>ラベル</SectionHeader>
          <AddButtonWrapper>
            <Button onClick={() => openItemModal(null)}>ラベルを追加</Button>
          </AddButtonWrapper>
          {sections.map((section) => (
            <div key={section.key}>
              {section.title !== null && <SubgroupCaption>{section.title}</SubgroupCaption>}
              {section.items.length === 0 ? (
                <EmptySectionText>このサブグループにはラベルがありません。</EmptySectionText>
              ) : (
                <Table styles={tableTextStyle}>
                  <Table.Header>
                    <Table.Row>
                      {itemHeaders.map((h) => (
                        <Table.Cell key={h.label} size={h.size}>
                          {h.label}
                        </Table.Cell>
                      ))}
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>{section.items.map(renderItemRow)}</Table.Body>
                </Table>
              )}
            </div>
          ))}
        </>
      )}

      {/* ラベル追加モーダル */}
      <Modal
        isOpen={isItemModalOpen}
        onRequestClose={closeItemModal}
        shouldCloseOnOverlayClick
        closeTimeoutMS={200}
        ariaHideApp={false}
        style={modalStyles}
      >
        <ModalTitle>新しいラベルの追加</ModalTitle>
        {hasSubgroups && (
          <FormRow>
            <FormLabel>サブグループ</FormLabel>
            <Select
              options={subgroupSelectOptions}
              value={
                addItemSubgroupId
                  ? { label: subgroupNameOf(addItemSubgroupId), value: addItemSubgroupId }
                  : { label: '未分類', value: UNGROUPED_VALUE }
              }
              onChange={(opt: { value: string } | null) =>
                setAddItemSubgroupId(opt && opt.value !== UNGROUPED_VALUE ? opt.value : null)
              }
              fullWidth
            />
          </FormRow>
        )}
        <TextArea
          placeholder="テキスト入力..."
          value={labelText}
          onChange={(e) => {
            setLabelText(e.target.value);
            setAddDuplicateError(false);
          }}
        />
        {addDuplicateError && (
          <ErrorMessage>
            <span>ラベル名は既に登録されています</span>
          </ErrorMessage>
        )}
        <ModalButtonGroup>
          <Button variant="neutral" onClick={closeItemModal} disabled={saving}>
            キャンセル
          </Button>
          <Button onClick={handleAddItem} disabled={saving || !labelText.trim()}>
            追加
          </Button>
        </ModalButtonGroup>
      </Modal>

      {/* ラベル名 変更確認モーダル */}
      <Modal
        isOpen={pendingNameUpdate !== null}
        onRequestClose={cancelNameUpdate}
        shouldCloseOnOverlayClick
        closeTimeoutMS={200}
        ariaHideApp={false}
        style={modalStyles}
      >
        <ModalTitle>ラベルを変更します</ModalTitle>
        <WarningMessage>
          <FA icon="exclamation-triangle" />
          <span>変更すると、このラベルを使用中の全店舗の表示に反映されます</span>
        </WarningMessage>
        <ModalButtonGroup>
          <Button variant="neutral" onClick={cancelNameUpdate}>
            キャンセル
          </Button>
          <Button onClick={confirmNameUpdate}>変更する</Button>
        </ModalButtonGroup>
      </Modal>

      {/* ラベル削除モーダル */}
      <Modal
        isOpen={deletingItem !== null}
        onRequestClose={closeDeleteItem}
        shouldCloseOnOverlayClick
        closeTimeoutMS={200}
        ariaHideApp={false}
        style={modalStyles}
      >
        <ModalTitle>ラベルを削除します</ModalTitle>
        <WarningMessage>
          <FA icon="exclamation-triangle" />
          <span>
            削除すると、このラベルは無効になります。すでに店舗に紐づいているラベルにも影響があります。
            元に戻せません。
          </span>
        </WarningMessage>
        <ModalButtonGroup>
          <Button variant="neutral" onClick={closeDeleteItem} disabled={saving}>
            キャンセル
          </Button>
          <Button variant="danger" onClick={handleDeleteItem} disabled={saving}>
            削除する
          </Button>
        </ModalButtonGroup>
      </Modal>

      {/* サブグループ追加モーダル */}
      <Modal
        isOpen={isSubgroupAddOpen}
        onRequestClose={closeSubgroupAdd}
        shouldCloseOnOverlayClick
        closeTimeoutMS={200}
        ariaHideApp={false}
        style={modalStyles}
      >
        <ModalTitle>サブグループを追加</ModalTitle>
        <FormRow>
          <FormLabel>サブグループ名</FormLabel>
          <Input
            fullWidth
            syncValue={subgroupName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setSubgroupName(e.target.value);
              setSubgroupAddError(null);
            }}
          />
        </FormRow>
        {subgroupAddError && (
          <ErrorMessage>
            <span>{subgroupAddError}</span>
          </ErrorMessage>
        )}
        <ModalButtonGroup>
          <Button variant="neutral" onClick={closeSubgroupAdd} disabled={saving}>
            キャンセル
          </Button>
          <Button onClick={handleAddSubgroup} disabled={saving || !subgroupName.trim()}>
            追加
          </Button>
        </ModalButtonGroup>
      </Modal>

      {/* サブグループ編集モーダル */}
      <Modal
        isOpen={editingSubgroup !== null}
        onRequestClose={closeSubgroupEdit}
        shouldCloseOnOverlayClick
        closeTimeoutMS={200}
        ariaHideApp={false}
        style={modalStyles}
      >
        <ModalTitle>サブグループを編集</ModalTitle>
        <FormRow>
          <FormLabel>サブグループ名</FormLabel>
          <Input
            fullWidth
            syncValue={editSubgroupName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setEditSubgroupName(e.target.value);
              setEditSubgroupError(null);
            }}
          />
        </FormRow>
        {editSubgroupError && (
          <ErrorMessage>
            <span>{editSubgroupError}</span>
          </ErrorMessage>
        )}
        <ModalButtonGroup>
          <Button variant="neutral" onClick={closeSubgroupEdit} disabled={saving}>
            キャンセル
          </Button>
          <Button onClick={handleEditSubgroup} disabled={saving || !editSubgroupName.trim()}>
            変更する
          </Button>
        </ModalButtonGroup>
      </Modal>

      {/* サブグループ削除モーダル */}
      <Modal
        isOpen={deletingSubgroup !== null}
        onRequestClose={closeSubgroupDelete}
        shouldCloseOnOverlayClick
        closeTimeoutMS={200}
        ariaHideApp={false}
        style={modalStyles}
      >
        <ModalTitle>サブグループを削除します</ModalTitle>
        <WarningMessage>
          <FA icon="exclamation-triangle" />
          <span>
            削除しても配下のラベルは削除されず「未分類（グループ直下）」に移動します。
            元に戻せません。
          </span>
        </WarningMessage>
        <ModalButtonGroup>
          <Button variant="neutral" onClick={closeSubgroupDelete} disabled={saving}>
            キャンセル
          </Button>
          <Button variant="danger" onClick={handleDeleteSubgroup} disabled={saving}>
            削除する
          </Button>
        </ModalButtonGroup>
      </Modal>

      {/* ラベル名 重複エラーモーダル */}
      <Modal
        isOpen={showDuplicateNameError}
        onRequestClose={() => setShowDuplicateNameError(false)}
        shouldCloseOnOverlayClick
        closeTimeoutMS={200}
        ariaHideApp={false}
        style={modalStyles}
      >
        <WarningMessage>
          <FA icon="exclamation-triangle" />
          <span>ラベル名は既に登録されています</span>
        </WarningMessage>
        <ModalButtonGroup>
          <Button variant="neutral" onClick={() => setShowDuplicateNameError(false)}>
            閉じる
          </Button>
        </ModalButtonGroup>
      </Modal>
    </>
  );
};

export default SettingsShopLabelDetail;
